import moment from 'moment';

import client from 'apollo/client';

import {loadClassroomNames} from '../hooks/useClassroomNames';
import {SkillRecommendationsAsEducatorDocument} from '../queries/SkillRecommendations.queries.generated';
import type {
  SkillRecommendationFieldsFragment,
  SkillRecommendationsAsEducatorQuery,
  SkillRecommendationsAsEducatorQueryVariables
} from '../queries/SkillRecommendations.queries.generated';
import type {
  ApprovedItem,
  ApproveRecommendationsInput,
  ApproveRecommendationsResult,
  AutomatedSkillPathsFilter,
  Recommendation
} from '../types';

const START_SPACING_DAYS = 3;
const DUE_AFTER_DAYS = 7;
const PAGE_SIZE = 250;

let approved: ApprovedItem[] = [];
/** Recommendation ids approve or dismiss took off the inbox, with their class. */
const handled = new Map<string, string>();
let nextId = 1;

/** A fresh copy whenever `handled` changes, so hooks can read it through `useSyncExternalStore`. */
let handledIds: ReadonlySet<string> = new Set();
const handledListeners = new Set<() => void>();
const handledChanged = () => {
  handledIds = new Set(handled.keys());
  handledListeners.forEach((listener) => listener());
};

export const subscribeToHandled = (listener: () => void) => {
  handledListeners.add(listener);
  return () => {
    handledListeners.delete(listener);
  };
};

/** The ids approve or dismiss took off the inbox. Stable until the next change. */
export const getHandledIds = () => handledIds;

/** FNV-1a scaled to [0, 1), so fulfilled items hold across reloads. */
const unitHash = (key: string) =>
  ([...key].reduce((h, char) => Math.imul(h ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0) /
  2 ** 32;

const isInScope = ({classroomIds}: AutomatedSkillPathsFilter, classroomId: string) =>
  !classroomIds || classroomIds.includes(classroomId);

const fulfilledAtOf = ({id, startDate}: ApprovedItem) =>
  startDate && !moment(startDate).isAfter() && unitHash(id) < 0.25 ? startDate : null;

/** Forgets in-scope handled ids the backend no longer proposes, so they aren't subtracted twice. */
export const forgetReplaced = (
  filter: AutomatedSkillPathsFilter,
  inbox: Pick<Recommendation, 'id'>[]
) => {
  const proposed = new Set(inbox.map(({id}) => id));
  const replaced = [...handled].filter(
    ([id, classroomId]) => isInScope(filter, classroomId) && !proposed.has(id)
  );
  if (replaced.length === 0) return;
  replaced.forEach(([id]) => handled.delete(id));
  handledChanged();
};

export const handledCountIn = (filter: AutomatedSkillPathsFilter) =>
  [...handled.values()].filter((classroomId) => isInScope(filter, classroomId)).length;

/** The rows behind `ids`, in path order, following the keyset until a short page. */
const fetchByIds = async (
  ids: string[],
  after?: string
): Promise<SkillRecommendationFieldsFragment[]> => {
  const {data} = await client.query<
    SkillRecommendationsAsEducatorQuery,
    SkillRecommendationsAsEducatorQueryVariables
  >({
    query: SkillRecommendationsAsEducatorDocument,
    variables: {filter: {id: {in: ids}}, first: PAGE_SIZE, after},
    fetchPolicy: 'network-only'
  });
  const page = data.skillRecommendationsAsEducator;
  const results = page?.results ?? [];
  const endKeyset = results.length === PAGE_SIZE ? page?.endKeyset : null;
  return endKeyset ? [...results, ...(await fetchByIds(ids, endKeyset))] : results;
};

/**
 * Reads the ticked and unticked rows back from the backend, keeps the ticked ones in memory as
 * approved items with made-up dates, and takes both sets off the inbox until the page reloads.
 */
export const approveRecommendations = async ({
  recommendationIds,
  dismissedIds
}: ApproveRecommendationsInput): Promise<ApproveRecommendationsResult> => {
  const ids = [...recommendationIds, ...dismissedIds];
  const [fetched, names] = await Promise.all([
    ids.length > 0 ? fetchByIds(ids) : Promise.resolve<SkillRecommendationFieldsFragment[]>([]),
    loadClassroomNames()
  ]);
  const rows = [...new Map(fetched.map((row) => [row.id, row])).values()].map(
    (row): Recommendation => ({
      ...row,
      classroom: {id: row.classroomId, name: names.get(row.classroomId) ?? 'Class'}
    })
  );
  const picked = rows.filter(({id}) => recommendationIds.includes(id));
  const approvedStudentIds = new Set(picked.map(({student}) => student.id));
  approved = approved.filter(
    (item) => !approvedStudentIds.has(item.student.id) || fulfilledAtOf(item) !== null
  );
  const today = moment().startOf('day');
  const created = picked.map((recommendation, index): ApprovedItem => {
    const order = picked
      .slice(0, index)
      .filter(({student}) => student.id === recommendation.student.id).length;
    const start = today.clone().add(order * START_SPACING_DAYS, 'days');
    return {
      ...recommendation,
      id: `approved-${nextId++}`,
      startDate: start.toISOString(),
      dueDate: start.clone().add(DUE_AFTER_DAYS, 'days').toISOString(),
      fulfilledAt: null
    };
  });
  rows.forEach(({id, classroom}) => handled.set(id, classroom.id));
  if (rows.length > 0) handledChanged();
  approved = [...approved, ...created];
  return {result: {approvedCount: created.length}, errors: []};
};

export const approvedSkills = async (filter: AutomatedSkillPathsFilter) =>
  approved
    .filter(({classroom}) => isInScope(filter, classroom.id))
    .map((item) => ({...item, fulfilledAt: fulfilledAtOf(item)}));
